import { streamOverviewsApi } from 'api-validations/tailor'
import { useMemo } from 'react'

import { useOrgCodeOrThrow } from '../../../active-operation'
import { usePanoramaQuery } from '../../../api-provider'
import { useLiveStreamData } from '../../../operation-wide-data/EmissaryWebsocketProvider'
import { type Stream, useAllStreams } from '../../../operation-wide-data/useAllStreams'
import { authoredAtOf, partitionKeyOf } from './stream-group'

export type GroupableStream = Stream & { authoredAt: Date }

const NONE: GroupableStream[] = []

const selectFinishedAt = (data: { items: { streamId: string; timeFinished: Date }[] }) =>
  new Map(data.items.map((item) => [item.streamId, item.timeFinished]))

export const useGroupableStreams = (
  streamId: string | null
): { streams: GroupableStream[]; isPending: boolean } => {
  const orgCode = useOrgCodeOrThrow()
  const allStreams = useAllStreams()
  const liveStreams = useLiveStreamData((d) => d.liveAccountConfig)

  const stream =
    streamId === null ? undefined : allStreams.data?.find((s) => s.streamId === streamId)

  const finishedAt = usePanoramaQuery(
    streamOverviewsApi,
    { orgCode, season: stream?.season ?? -1, crop: stream?.cropKey ?? '' },
    { enabled: stream !== undefined, select: selectFinishedAt }
  )

  const streams = useMemo(() => {
    if (stream === undefined || finishedAt.isPending) return NONE
    const scope = partitionKeyOf(stream)
    return (allStreams.data ?? [])
      .filter((s) => partitionKeyOf(s) === scope)
      .map((s) => ({
        ...s,
        authoredAt: authoredAtOf({ stream: s, finishedAt: finishedAt.data, liveStreams })
      }))
  }, [stream, finishedAt.isPending, finishedAt.data, allStreams.data, liveStreams])

  return { streams, isPending: streamId !== null && finishedAt.isPending }
}
